import React, { useEffect, useRef, useState } from 'react';

const steps = [
  {
    num: '01',
    title: 'Discovery Call',
    description:
      'We audit your current setup, understand your ideal client, and identify the fastest path to predictable revenue.',
  },
  {
    num: '02',
    title: 'System Design',
    description:
      'We architect a custom growth system — funnels, ad strategy, CRM flows — tailored to your business model and market.',
  },
  {
    num: '03',
    title: 'Build & Launch',
    description:
      'Our team builds and deploys every component — from landing pages to automation workflows — within agreed timelines.',
  },
  {
    num: '04',
    title: 'Optimize & Scale',
    description:
      'We monitor KPIs weekly, run split tests, and continuously scale what\'s working — compounding your results over time.',
  },
];

const Process = () => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="process" className="py-24 px-6 bg-[#0a0e1a] relative overflow-hidden" ref={sectionRef}>
      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(30px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .process-animate {
          opacity: 0;
        }
        .process-animate.visible {
          animation: fadeInUp 0.7s ease-out forwards;
        }
        .step-connector::before {
          content: '';
          position: absolute;
          top: 26px;
          left: calc(50% + 26px);
          right: calc(-50% + 26px);
          height: 1px;
          background: linear-gradient(90deg, rgba(0,123,255,0.4), rgba(0,123,255,0.1));
        }
      `}</style>

      {/* Background glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[300px] bg-[#007bff]/5 blur-[120px] rounded-full pointer-events-none" />

      <div className="max-w-6xl mx-auto relative z-10">
        {/* Header */}
        <div className={`text-center mb-16 process-animate ${isVisible ? 'visible' : ''}`}>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#007bff] mb-3">
            How It Works
          </p>
          <h2 className="text-3xl md:text-5xl font-extrabold text-white mb-4 tracking-tight">
            From Consultation to{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#0099ff] to-[#00ccff]">
              Scaled Revenue
            </span>
          </h2>
          <p className="text-gray-400 text-lg max-w-xl mx-auto">
            A clear four-step process to get your growth system live and generating results.
          </p>
        </div>

        {/* Steps */}
        <div className="grid md:grid-cols-4 gap-8 relative">
          {/* Connector line (desktop only) */}
          <div className="absolute top-[26px] left-[12%] right-[12%] h-px bg-gradient-to-r from-transparent via-[#007bff]/30 to-transparent hidden md:block pointer-events-none" />

          {steps.map((step, index) => (
            <div
              key={index}
              className={`process-animate ${isVisible ? 'visible' : ''} text-center relative`}
              style={isVisible ? { animationDelay: `${0.1 + index * 0.15}s` } : {}}
            >
              {/* Step number circle */}
              <div className="w-[52px] h-[52px] bg-[#020617] border border-[#007bff]/60 rounded-full flex items-center justify-center mx-auto mb-6 relative z-10">
                <span className="font-extrabold text-sm text-[#007bff]">{step.num}</span>
              </div>
              <h4 className="text-white font-bold text-lg mb-3">{step.title}</h4>
              <p className="text-gray-400 text-sm leading-relaxed">{step.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Process;
